import { Box, Typography, InputBase, IconButton, CircularProgress, Chip } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const ChatAreaContainer = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background-color: ${theme.colors.modalSurface};
`

export const ErrorAlert = styled(Box)`
  margin: 0 ${theme.spacing.medium}px;
  margin-top: ${theme.spacing.small}px;
  padding: ${theme.spacing.small}px;
  background-color: ${theme.colors.errorLight};
  border-radius: ${theme.attributes.borderRadius.secondary};
  color: ${theme.colors.primaryDark};
`

export const ErrorText = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
`

interface ContentAreaProps {
  hasMessages: boolean
}

export const ContentArea = styled(Box)<ContentAreaProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: ${({ hasMessages }) => (hasMessages ? 'flex-start' : 'center')};
  align-items: center;
  overflow: auto;
  padding: ${({ hasMessages }) => (hasMessages ? theme.spacing.medium : 0)}px;
`

export const MessagesContainer = styled(Box)`
  width: 100%;
  max-width: 800px;
`

export const WelcomeContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 80px;
  width: 100%;
  max-width: 800px;
`

export const WelcomeHeading = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 40px;
  font-weight: ${theme.typography.fontWeight.extraThick};
  color: ${theme.colors.menuItemText};
  text-align: center;
  line-height: 48px;
`

export const InputContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
  width: 100%;
  max-width: 736px;
`

export const InputBox = styled(Box)`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.legacyBorderLow2};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background-color: transparent;
  padding: ${theme.spacing.xSmall}px ${theme.spacing.xSmall}px 0;
  min-height: 128px;

  &:focus-within {
    border: 1px solid ${theme.colors.cta};
    border-radius: 12px;
  }
`

export const InputBoxCompact = styled(Box)`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.legacyBorderLow2};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background-color: transparent;
  padding: ${theme.spacing.xSmall}px ${theme.spacing.xSmall}px 0;
  max-width: 800px;
  margin: 0 auto;

  &:focus-within {
    border: 1px solid ${theme.colors.cta};
    border-radius: 12px;
  }
`

export const InputWrapper = styled(Box)`
  flex: 1;
  padding: ${theme.spacing.xSmall}px;
  padding-top: ${theme.spacing.xSmall}px;
`

export const ChatInput = styled(InputBase)`
  width: 100%;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.menuItemText};
  line-height: 20px;

  & input::placeholder,
  & textarea::placeholder {
    color: ${theme.colors.calendarDay} !important;
    -webkit-text-fill-color: ${theme.colors.calendarDay} !important;
    opacity: 1 !important;
  }

  & input:focus::placeholder,
  & textarea:focus::placeholder {
    opacity: 0 !important;
  }

  &.Mui-disabled {
    color: ${theme.colors.textSecondary};
    -webkit-text-fill-color: ${theme.colors.calendarDay};

    & input,
    & textarea {
      color: ${theme.colors.calendarDay} !important;
      -webkit-text-fill-color: ${theme.colors.calendarDay} !important;
    }

    & input::placeholder,
    & textarea::placeholder {
      color: ${theme.colors.calendarDay} !important;
      -webkit-text-fill-color: ${theme.colors.calendarDay} !important;
      opacity: 1 !important;
    }
  }
`

export const InputActions = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 0 8px;
`

export const InputActionsCompact = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${theme.spacing.xSmall}px ${theme.spacing.xSmall}px 8px;
`

export const AttachButton = styled(IconButton)`
  padding: 6px;
`

export const SendButton = styled(IconButton)`
  background-color: ${theme.colors.cta};
  color: ${theme.colors.surfaceContainerBackground};
  width: 32px;
  height: 32px;
  border-radius: 8px;

  &:hover {
    background-color: ${theme.colors.buttonHollowHover};
  }

  &.Mui-disabled {
    background-color: #946592;
    color: ${theme.colors.surfaceContainerBackground};
  }

  html[data-theme='light'] & {
    background-color: #a13b7f;
    color: #fdfdfd;

    &:hover {
      background-color: #c35ba0;
      color: #fdfdfd;
    }

    &:focus {
      background-color: #60234c;
      color: #fa75df;
    }

    &.Mui-disabled {
      background-color: #cb95b9;
      color: #ffebfb;
    }
  }
`

export const StopButton = styled(IconButton)`
  background-color: ${theme.colors.legacy483646};
  color: ${theme.colors.legacyTextPrimary3};
  width: 32px;
  height: 32px;
  border-radius: 8px;

  &:hover {
    background-color: ${theme.colors.legacy573f56};
  }

  html[data-theme='light'] & {
    background-color: #a13b7f;
    color: #fdfdfd;

    &:hover {
      background-color: #c35ba0;
    }
  }
`

export const SuggestionsContainer = styled(Box)`
  display: flex;
  justify-content: center;
  flex-flow: row wrap;
  gap: ${theme.spacing.xSmall}px;
`

export const SuggestionChip = styled(Box)`
  padding: ${theme.spacing.small}px ${theme.spacing.small}px;
  background-color: ${theme.colors.surfaceContainerBackground};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  cursor: pointer;

  &:hover {
    border-color: ${theme.colors.legacyBorderLow2};
    background-color: ${theme.colors.surfaceItemBackgroundLow};
  }

  html[data-theme='light'] & {
    &:hover {
      background-color: ${theme.colors.cardFillTertiary};

      .MuiTypography-root {
        color: ${theme.colors.menuItemText};
      }
    }
  }
`

export const SuggestionText = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.menuItemText};
  white-space: nowrap;
`

export const BottomInputContainer = styled(Box)`
  padding: 0 ${theme.spacing.medium}px ${theme.spacing.medium}px;
  padding-top: ${theme.spacing.small}px;
`

// Message styles
interface MessageContainerProps {
  isUser: boolean
}

export const MessageContainer = styled(Box)<MessageContainerProps>`
  margin-bottom: ${theme.spacing.small}px;
  padding: ${({ isUser }) => (isUser ? '12px 16px' : '16px')};
  border-radius: ${({ isUser }) => (isUser ? '12px' : '8px')};
  background-color: ${({ isUser }) => (isUser ? theme.colors.legacySurfaceContainerBackground2 : 'transparent')};
  max-width: ${({ isUser }) => (isUser ? '70%' : '100%')};
  margin-left: ${({ isUser }) => (isUser ? 'auto' : 0)};
`

export const UserMessageText = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.foreground};
  line-height: 20px;
  white-space: pre-wrap;
`

export const MessageRole = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.medium};
  color: ${theme.colors.cardDateText};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing.xSmall}px;
  line-height: 20px;
`

export const StreamingIndicator = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const StreamingText = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  color: ${theme.colors.legacyNeutral100};
`

// Animated loading bars
export const LoadingBarsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;

  @keyframes loadingPulse {
    0%,
    100% {
      transform: scaleY(0.6);
      opacity: 0.6;
    }
    50% {
      transform: scaleY(1);
      opacity: 1;
    }
  }
`

export const LoadingBar = styled(Box)<{ isActive?: boolean; delay?: number }>`
  width: 3.4px;
  height: ${({ isActive }) => (isActive ? '24px' : '14.4px')};
  background-color: ${({ isActive }) => (isActive ? theme.colors.cta : theme.colors.legacy573f562)};
  border-radius: 1.7px;
  animation: loadingPulse 1s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || 0}ms;
`

// Thinking section styles
export const ThinkingHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xSmall}px;
  cursor: pointer;
  color: ${theme.colors.legacyNeutral100};

  &:hover {
    color: ${theme.colors.menuItemText};
  }
`

export const ThinkingTitle = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.medium};
  font-weight: ${theme.typography.fontWeight.bold};
`

export const ThinkingContent = styled(Box)`
  margin-top: ${theme.spacing.xSmall}px;
  padding: ${theme.spacing.small}px;
  background-color: ${theme.colors.legacySurfaceItemBackground};
  border-radius: 4px;
  border-left: 3px solid ${theme.colors.legacyNeutral100};
  max-height: 200px;
  overflow: auto;
`

export const ThinkingText = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.medium};
  color: ${theme.colors.legacyNeutral100};
  white-space: pre-wrap;
`

// Status indicator styles
export const StatusIndicator = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xSmall}px;
  padding: ${theme.spacing.small}px;
`

export const StatusSpinner = styled(CircularProgress)`
  color: ${theme.colors.cta};
`

export const StatusChip = styled(Chip)`
  background-color: ${theme.colors.legacySurfaceItemBackground};
  color: ${theme.colors.menuItemText};
  font-family: ${theme.typography.fontFamily.primary};
`

export const StreamingSpinner = styled(CircularProgress)`
  color: ${theme.colors.cta};
`

// Chart container
export const ChartContainer = styled(Box)`
  margin: ${theme.spacing.small}px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const ChartTitle = styled(Typography)`
  color: ${theme.colors.menuItemText};
  margin-bottom: ${theme.spacing.xSmall}px;
`

// Thinking section wrapper
export const ThinkingSectionWrapper = styled(Box)`
  margin-bottom: 16px;
`

// Markdown styles for ReactMarkdown components
export const markdownStyles = {
  h1: {
    color: theme.colors.menuItemText,
    marginTop: 16,
    marginBottom: 24,
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: '18px',
    fontWeight: theme.typography.fontWeight.extraThick,
    lineHeight: '20px'
  },
  h2: {
    color: theme.colors.menuItemText,
    marginTop: 20,
    marginBottom: 12,
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xLarge,
    fontStyle: 'normal',
    fontWeight: theme.typography.fontWeight.extraThick,
    lineHeight: '20px'
  },
  h3: {
    color: theme.colors.cta,
    marginTop: 16,
    marginBottom: 12,
    fontWeight: theme.typography.fontWeight.extraThick,
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: '18px'
  },
  p: {
    color: theme.colors.menuItemText,
    margin: `${theme.spacing.half}px 0`,
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.large,
    fontStyle: 'normal',
    fontWeight: theme.typography.fontWeight.bold,
    lineHeight: '20px'
  },
  strong: {
    color: theme.colors.menuItemText
  },
  ul: {
    margin: '8px 0',
    paddingLeft: '20px'
  },
  ol: {
    margin: '8px 0',
    paddingLeft: '20px'
  },
  li: {
    color: theme.colors.menuItemText,
    marginBottom: `${theme.spacing.half}px`,
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.large,
    fontStyle: 'normal',
    fontWeight: theme.typography.fontWeight.normal,
    lineHeight: '20px'
  },
  blockquote: {
    borderLeft: `3px solid ${theme.colors.cta}`,
    paddingLeft: 16,
    margin: '8px 0',
    color: theme.colors.legacyNeutral100
  },
  inlineCode: {
    backgroundColor: theme.colors.legacySurfaceItemBackground,
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '12px',
    color: theme.colors.cta
  },
  codeBlock: {
    backgroundColor: theme.colors.legacySurfaceItemBackground,
    padding: 16,
    borderRadius: 4,
    overflow: 'auto' as const,
    margin: '8px 0'
  },
  code: {
    fontSize: '12px',
    color: theme.colors.menuItemText
  }
}

// Icon sizes
export const iconStyles = {
  small: {
    fontSize: 18
  },
  medium: {
    fontSize: 20
  },
  sendIcon: {
    fontSize: 20,
    transform: 'rotate(-90deg)'
  }
}
